<template>
  <div class="page-container" :class="{ 'is-prepare': viewMode === 'prepare' }">
    <header class="header">
      <el-button text class="back-btn" aria-label="返回" @click="handleBack">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">{{ viewMode === "prepare" ? "扫码校验" : "扫码出库" }}</h1>
      <div class="header-right"></div>
    </header>

    <!-- 列表：扫送货单号 -->
    <main v-if="viewMode === 'list'" class="content">
      <el-card shadow="never" class="block-card compact-card">
        <el-row :gutter="8" align="middle">
          <el-col :span="18">
            <el-input
              ref="sendInputRef"
              v-model="scanCode"
              size="large"
              placeholder="扫描送货单号"
              clearable
              :disabled="loading"
              @keyup.enter="handleSendScanEnter"
              @clear="focusSendInput"
            />
          </el-col>
          <el-col :span="6">
            <el-button
              type="primary"
              size="large"
              class="full-btn"
              :disabled="loading || (!scannedSendNo && list.length === 0)"
              @click="handleListReset"
            >
              重置
            </el-button>
          </el-col>
        </el-row>
      </el-card>

      <div
        v-loading="loading"
        element-loading-text="查询中..."
        class="list-wrap"
      >
        <el-empty
          v-if="!hasScanned && !loading"
          description="请用 PDA 扫描送货单号"
        />
        <el-empty
          v-else-if="hasScanned && list.length === 0 && !loading"
          description="未查到待送货计划单"
        />

        <el-space
          v-else
          direction="vertical"
          fill
          :size="8"
          style="width: 100%"
        >
          <el-card
            v-for="item in list"
            :key="item.id"
            shadow="never"
            class="compact-card"
          >
            <div class="order-head">
              <el-tag size="small" type="danger" effect="light">待扫码</el-tag>
              <el-text tag="b" class="send-no">
                {{ item.sendNo || scannedSendNo || "-" }}
              </el-text>
            </div>
            <el-text size="small" type="info" class="order-meta">
              {{ item.supplierName || "-" }}
            </el-text>
            <el-text size="small" type="info" class="order-meta">
              创建时间：{{ formatTime(item.createTime) }}
            </el-text>
            <el-row :gutter="8" class="action-row">
              <el-col :span="12">
                <el-button
                  type="primary"
                  class="full-btn"
                  :loading="
                    prepareLoading &&
                    preparingId === item.id &&
                    preparingDirection === 'asc'
                  "
                  :disabled="prepareLoading || !isTodayCreate(item.createTime)"
                  @click="startPrepare(item, 'asc')"
                >
                  从上到下 1-n
                </el-button>
              </el-col>
              <el-col :span="12">
                <el-button
                  class="full-btn"
                  :loading="
                    prepareLoading &&
                    preparingId === item.id &&
                    preparingDirection === 'desc'
                  "
                  :disabled="prepareLoading || !isTodayCreate(item.createTime)"
                  @click="startPrepare(item, 'desc')"
                >
                  从下到上 n-1
                </el-button>
              </el-col>
            </el-row>
          </el-card>
        </el-space>
      </div>
    </main>

    <!-- 备料：扫箱码校验物料 -->
    <main v-else class="content prepare-content">
      <el-card shadow="never" class="block-card compact-card sticky-scan">
        <div class="prepare-head">
          <el-text truncated class="send-no">
            {{ detail?.sendNo || scannedSendNo || "-" }}
          </el-text>
          <el-tag size="small" effect="plain">{{ scanDirectionLabel }}</el-tag>
        </div>
        <el-row :gutter="8" align="middle" class="progress-row">
          <el-col :span="16">
            <el-text size="small">
              已校验 {{ matchedCount }}/{{ materialList.length }}
            </el-text>
          </el-col>
          <el-col :span="8" class="tag-end">
            <el-tag
              size="small"
              :type="qrConfigReady ? 'success' : 'danger'"
              effect="plain"
            >
              {{ qrConfigReady ? "规则就绪" : "规则未加载" }}
            </el-tag>
          </el-col>
        </el-row>
        <el-progress
          :percentage="progressPercent"
          :stroke-width="4"
          :status="allMatched ? 'success' : undefined"
          :show-text="false"
        />
        <el-input
          ref="boxInputRef"
          v-model="boxCode"
          class="box-input"
          size="large"
          placeholder="扫描箱码校验编码"
          clearable
          :disabled="!qrConfigReady || prepareLoading || allMatched"
          @keyup.enter="handleBoxScanEnter"
        />
        <el-text
          v-if="allMatched"
          type="success"
          size="small"
          class="scan-hint"
        >
          全部已校验，可确认完成
        </el-text>
        <el-text v-else-if="currentScanHint" size="small" class="scan-hint">
          {{ currentScanHint }}{{ hasQtyRule ? "，并扫齐数量" : "" }}
        </el-text>
      </el-card>

      <div
        v-loading="prepareLoading"
        element-loading-text="加载中..."
        class="list-wrap"
      >
        <el-empty
          v-if="!prepareLoading && materialList.length === 0"
          description="暂无物料明细"
        />

        <el-space
          v-else
          direction="vertical"
          fill
          :size="6"
          style="width: 100%"
        >
          <div
            v-for="(item, index) in displayMaterials"
            :key="item.uid"
            class="material-row"
            :class="{
              matched: item.matched,
              failed: item.lastFail,
              current: isCurrentTarget(item),
              later: isLaterTarget(item),
            }"
          >
            <el-tag
              size="small"
              :type="item.matched ? 'success' : 'primary'"
              class="sort-tag"
            >
              {{ item.matched ? "✓" : item.detailSort || index + 1 }}
            </el-tag>
            <div class="material-main">
              <el-text tag="b" class="material-no">
                {{ item.materialNo || "-" }}
              </el-text>
              <el-text v-if="hasQtyRule" size="small" type="info">
                {{ formatQty(item.scannedQty) }}/{{
                  formatQty(getExpectedQty(item))
                }}
              </el-text>
            </div>
            <el-tag
              size="small"
              :type="item.matched ? 'success' : 'info'"
              effect="plain"
            >
              {{ item.matched ? "已扫" : "待扫" }}
            </el-tag>
          </div>
        </el-space>
      </div>

      <footer class="action-bar">
        <el-text :type="allMatched ? 'success' : 'info'">
          <template v-if="allMatched">可以校验完成</template>
          <template v-else>
            还需扫码 {{ materialList.length - matchedCount }} 项
          </template>
        </el-text>
        <el-button
          type="primary"
          size="large"
          :disabled="!canSubmitWareOut"
          :loading="submitLoading"
          @click="handleWareOut"
        >
          {{ submitLoading ? "提交中..." : "校验完成" }}
        </el-button>
      </footer>
    </main>

    <el-dialog
      :model-value="submitLoading"
      title="正在提交校验"
      width="260px"
      align-center
      append-to-body
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <el-text>请稍候，约需几秒钟...</el-text>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  getSendPlanBySendNo,
  getSendPlanDetail,
  distributeSendPlanAuto,
} from "@/api/wareOut";
import { getSupplierPageGtZero } from "@/api/wareInReceiveCreate";

const router = useRouter();

const viewMode = ref("list"); // list | prepare

const sendInputRef = ref(null);
const boxInputRef = ref(null);
const scanCode = ref("");
const scannedSendNo = ref("");
const hasScanned = ref(false);
const loading = ref(false);
const list = ref([]);
const total = ref(0);

const prepareLoading = ref(false);
const preparingId = ref("");
const preparingDirection = ref("");
/** asc 从上到下：1-1、2-1、3-1；desc 从下到上：3-1、2-1、1-1 */
const scanDirection = ref("asc");
const detail = ref(null);
const supplierInfo = ref(null);
const qrConfig = ref(null);
const materialList = ref([]);
const boxCode = ref("");
const scannedBoxKeys = ref([]);
const submitLoading = ref(false);

const qrConfigReady = computed(() => Boolean(qrConfig.value));
/** 规则数量下标不是 -1 时，需要校验扫码数量 */
const hasQtyRule = computed(
  () => Number(qrConfig.value?.materialNumIndex) >= 0
);

function parseDate(val) {
  if (!val) return null;
  const raw = typeof val === "string" ? val : String(val);
  const safe =
    raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const d = new Date(safe);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTime(val) {
  const d = parseDate(val);
  if (!d) return val ? String(val) : "-";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

/** 仅当天创建的单据可进入扫码 */
function isTodayCreate(val) {
  const d = parseDate(val);
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}
const matchedCount = computed(
  () => materialList.value.filter((m) => m.matched).length
);
const allMatched = computed(
  () =>
    materialList.value.length > 0 &&
    matchedCount.value >= materialList.value.length
);
const canSubmitWareOut = computed(
  () => allMatched.value && !submitLoading.value && !prepareLoading.value
);
const progressPercent = computed(() => {
  const totalCount = materialList.value.length;
  if (!totalCount) return 0;
  return Math.min(100, Math.round((matchedCount.value / totalCount) * 100));
});
/**
 * detailSort 形如 1-1、2-1、1-2。
 * 横线后面是批次：先扫全部 x-1，再扫全部 x-2。
 * 同一批次里，从上到下按序号从小到大，从下到上按序号从大到小。
 */
function parseDetailSort(raw) {
  const text = String(raw ?? "").trim();
  const matched = text.match(/^(\d+)\s*[-－]\s*(\d+)$/);
  if (!matched) {
    return {
      text,
      seq: Number.MAX_SAFE_INTEGER,
      wave: Number.MAX_SAFE_INTEGER,
      valid: false,
    };
  }
  return {
    text,
    seq: Number(matched[1]),
    wave: Number(matched[2]),
    valid: true,
  };
}

const scanDirectionLabel = computed(() =>
  scanDirection.value === "desc" ? "从下到上 n-1" : "从上到下 1-n"
);

function compareByDetailSort(a, b) {
  const waveDiff = a.sortParts.wave - b.sortParts.wave;
  if (waveDiff !== 0) return waveDiff;
  const seqDiff = a.sortParts.seq - b.sortParts.seq;
  const directed = scanDirection.value === "desc" ? -seqDiff : seqDiff;
  if (directed !== 0) return directed;
  return a.originIndex - b.originIndex;
}

/**
 * 严格按明细顺序扫：始终只认当前第一条未扫明细。
 * 相同物料编码也不会跳到下一条，数量只累计到当前这条。
 */
const nextScanItem = computed(() =>
  materialList.value.find((item) => !item.matched) || null
);

const currentScanHint = computed(() => {
  const item = nextScanItem.value;
  if (!item) return "";
  const sort = item.detailSort ? `${item.detailSort} ` : "";
  return `请扫描 ${sort}${item.materialNo || ""}`.trim();
});

/** 按所选方向展示：先全部 x-1，再全部 x-2 */
const displayMaterials = computed(() => materialList.value);

function isCurrentTarget(item) {
  return Boolean(item && nextScanItem.value && item.uid === nextScanItem.value.uid);
}

function isLaterTarget(item) {
  return Boolean(item && !item.matched && nextScanItem.value && item.uid !== nextScanItem.value.uid);
}

function toPositiveNumber(val) {
  if (val === "" || val == null) return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function getExpectedQty(item) {
  const demand = toPositiveNumber(item?.demandNum);
  if (demand != null && demand > 0) return demand;
  const wareOut = toPositiveNumber(item?.wareOutNum);
  if (wareOut != null && wareOut > 0) return wareOut;
  const prepare = toPositiveNumber(item?.prepareNum);
  if (prepare != null && prepare > 0) return prepare;
  return 0;
}

/** 兼容非标准 JSON：{separator: "]", materialNoIndex: 3} */
function tryParseJsonLoose(s) {
  if (!s || typeof s !== "string") return null;
  const trimmed = s.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    try {
      const normalized = trimmed
        .replace(/\r?\n/g, " ")
        .replace(/([{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":');
      return JSON.parse(normalized);
    } catch (e2) {
      return null;
    }
  }
}

function normalizeQrConfig(raw) {
  const parsed = tryParseJsonLoose(raw);
  if (!parsed) return null;

  const materialNoIndex = Number(
    parsed.materialNoIndex ?? parsed.materialCodeIndex ?? parsed.materialNodex
  );
  const materialNumIndex = Number(parsed.materialNumIndex ?? parsed.qtyIndex);
  const supplierCodeIndex = Number(parsed.supplierCodeIndex);
  const batchNoIndex = Number(parsed.batchNoIndex);
  const boxIndexIndex = Number(
    parsed.boxIndexIndex ??
      (Number.isFinite(Number(parsed.boxIndex)) ? parsed.boxIndex : NaN)
  );
  // 二级截断符：如 键#值$ 场景，先按 # 取段，再按 $ 截取真正的值
  const valueEndSeparator =
    parsed.valueEndSeparator ??
    parsed.endSeparator ??
    parsed.trimSeparator ??
    "";

  return {
    separator: parsed.separator != null ? String(parsed.separator) : "|",
    valueEndSeparator:
      valueEndSeparator != null && String(valueEndSeparator) !== ""
        ? String(valueEndSeparator)
        : "",
    supplierCodeIndex: Number.isFinite(supplierCodeIndex)
      ? supplierCodeIndex
      : -1,
    materialNoIndex: Number.isFinite(materialNoIndex) ? materialNoIndex : -1,
    materialNumIndex: Number.isFinite(materialNumIndex) ? materialNumIndex : -1,
    batchNoIndex: Number.isFinite(batchNoIndex) ? batchNoIndex : -1,
    boxIndexIndex: Number.isFinite(boxIndexIndex) ? boxIndexIndex : -1,
  };
}

function extractBoxIndexFromRaw(text) {
  const m = String(text || "").match(/boxIndex\s*[=:：]\s*([^;|\]\s]+)/i);
  return m ? String(m[1] || "").trim() : "";
}

function parseBoxByConfig(text, cfg) {
  const sep = String(cfg?.separator || "|");
  const endSep = String(cfg?.valueEndSeparator || "");
  const parts = String(text || "").split(sep);
  const pick = (index) => {
    if (!Number.isFinite(index) || index < 0) return "";
    let val = String(parts[index] ?? "").trim();
    // 例如取到 602003098AA$11，再按 $ 截成 602003098AA
    if (endSep && val.includes(endSep)) {
      val = val.split(endSep)[0].trim();
    }
    return val;
  };
  const boxIndexFromCfg = pick(cfg.boxIndexIndex);
  const boxIndex = boxIndexFromCfg || extractBoxIndexFromRaw(text);
  return {
    supplierCode: pick(cfg.supplierCodeIndex),
    materialNo: pick(cfg.materialNoIndex),
    num: pick(cfg.materialNumIndex),
    batchNo: pick(cfg.batchNoIndex),
    boxIndex,
    parts,
  };
}

/** 数量展示去掉多余小数：12.000000 -> 12 */
function formatQty(val) {
  if (val === null || val === undefined || val === "") return "-";
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val);
  if (Number.isInteger(n)) return String(n);
  return String(parseFloat(n.toFixed(6)));
}

function normalizeScanText(raw) {
  return String(raw || "")
    .replace(/[\r\n\t]/g, "")
    .trim();
}

// 扫码提示音 + 语音播报
let audioContext = null;
let speechReady = false;

const initAudioContext = () => {
  if (audioContext) return;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return;
    audioContext = new Ctor();
    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {});
    }
  } catch (e) {
    console.warn("创建音频上下文失败", e);
  }
};

const initSpeech = () => {
  if (speechReady || !("speechSynthesis" in window)) return;
  try {
    const warm = new SpeechSynthesisUtterance("");
    warm.volume = 0;
    warm.lang = "zh-CN";
    window.speechSynthesis.speak(warm);
    window.speechSynthesis.cancel();
    speechReady = true;
  } catch (e) {
    console.warn("初始化语音失败", e);
  }
};

const unlockAudio = () => {
  initAudioContext();
  initSpeech();
};

const ensureAudioReady = () => {
  initAudioContext();
  if (!audioContext) return null;
  if (audioContext.state === "suspended") {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
};

const getZhVoice = () => {
  try {
    const voices = window.speechSynthesis?.getVoices?.() || [];
    return (
      voices.find((v) => v.lang === "zh-CN") ||
      voices.find((v) => String(v.lang || "").startsWith("zh")) ||
      null
    );
  } catch (e) {
    return null;
  }
};

const speak = (text) => {
  if (!text || !("speechSynthesis" in window)) return;
  try {
    initSpeech();
    window.speechSynthesis.cancel();
    const speech = new SpeechSynthesisUtterance(String(text));
    speech.lang = "zh-CN";
    speech.volume = 1;
    speech.rate = 1.15;
    speech.pitch = 1;
    const zhVoice = getZhVoice();
    if (zhVoice) speech.voice = zhVoice;
    window.speechSynthesis.speak(speech);
  } catch (e) {
    console.warn("语音播报失败", e);
  }
};

const playSuccessBeep = () => {
  try {
    const ctx = ensureAudioReady();
    if (!ctx) return;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(1000, ctx.currentTime);
    gainNode.gain.setValueAtTime(1, ctx.currentTime);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.3);
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  } catch (e) {
    console.warn("播放成功提示音失败", e);
  }
};

const playErrorBeep = () => {
  try {
    const ctx = ensureAudioReady();
    if (!ctx) return;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.type = "square";
    const t = ctx.currentTime;
    oscillator.frequency.setValueAtTime(400, t);
    gainNode.gain.setValueAtTime(0, t);
    gainNode.gain.setValueAtTime(0.8, t);
    gainNode.gain.setValueAtTime(0, t + 0.1);
    gainNode.gain.setValueAtTime(0.8, t + 0.16);
    gainNode.gain.setValueAtTime(0, t + 0.26);
    gainNode.gain.setValueAtTime(0.8, t + 0.32);
    gainNode.gain.setValueAtTime(0, t + 0.42);
    oscillator.start(t);
    oscillator.stop(t + 0.44);
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  } catch (e) {
    console.warn("播放失败提示音失败", e);
  }
};

const notifySuccess = (voiceText = "匹配成功") => {
  playSuccessBeep();
  speak(voiceText);
};

const notifyError = (voiceText = "校验失败") => {
  playErrorBeep();
  speak(voiceText);
};

const focusSendInput = async () => {
  await nextTick();
  const inputComp = sendInputRef.value;
  const inputEl = inputComp?.$el?.querySelector?.("input");
  if (inputEl) inputEl.focus();
  else if (typeof inputComp?.focus === "function") inputComp.focus();
};

const focusBoxInput = async () => {
  await nextTick();
  const inputComp = boxInputRef.value;
  const inputEl = inputComp?.$el?.querySelector?.("input");
  if (inputEl) inputEl.focus();
  else if (typeof inputComp?.focus === "function") inputComp.focus();
};

const fetchBySendNo = async (sendNo) => {
  const code = normalizeScanText(sendNo);
  if (!code) {
    ElMessage.warning("请先扫描送货单号");
    focusSendInput();
    return;
  }

  loading.value = true;
  hasScanned.value = true;
  scannedSendNo.value = code;
  scanCode.value = "";

  try {
    const res = await getSendPlanBySendNo(code);
    if (res?.success === false) {
      throw new Error(res?.msg || res?.message || "查询失败");
    }
    const data = res?.data;
    const records = data ? (Array.isArray(data) ? data : [data]) : [];
    total.value = records.length;
    list.value = records;

    if (records.length === 0) {
      ElMessage.info("未查询到待送货计划单");
    }
  } catch (e) {
    console.error("扫码出库查询失败:", e);
    list.value = [];
    total.value = 0;
    ElMessage.error(e?.message || "查询失败，请重试");
  } finally {
    loading.value = false;
    focusSendInput();
  }
};

const handleSendScanEnter = () => {
  if (loading.value) return;
  unlockAudio();
  fetchBySendNo(scanCode.value);
};

const handleListReset = () => {
  scanCode.value = "";
  scannedSendNo.value = "";
  hasScanned.value = false;
  list.value = [];
  total.value = 0;
  focusSendInput();
};

const fetchSupplierConfig = async (supplierId) => {
  const res = await getSupplierPageGtZero({
    id: supplierId,
    current: 1,
    size: 20,
  });
  const records = res?.data?.records || [];
  return (
    records.find((r) => String(r?.id) === String(supplierId)) ||
    records[0] ||
    null
  );
};

const buildMaterialList = (detailData) => {
  const rows =
    detailData?.sendDetailList ||
    detailData?.sendPlanDetailList ||
    detailData?.planDetailList ||
    detailData?.outDetailList ||
    detailData?.wareOutDetailList ||
    detailData?.detailList ||
    [];
  return (Array.isArray(rows) ? rows : [])
    .map((row, index) => {
      const sortParts = parseDetailSort(row.detailSort);
      return {
        uid: String(row.id || `${row.materialNo || "m"}-${index}`),
        id: row.id,
        materialId: row.materialId,
        materialNo: String(row.materialNo || "").trim(),
        materialDesc: row.materialDesc || "",
        demandNum: row.demandNum,
        wareOutNum: row.wareOutNum,
        prepareNum: row.prepareNum,
        detailSort: sortParts.text,
        sortParts,
        originIndex: index,
        raw: row,
        matched: false,
        lastFail: false,
        scannedMaterialNo: "",
        scannedQty: 0,
      };
    })
    .sort(compareByDetailSort);
};

const startPrepare = async (item, direction = "asc") => {
  if (!item?.id || prepareLoading.value) return;
  if (!isTodayCreate(item.createTime)) {
    ElMessage.warning("仅当天创建的送货单可扫码");
    return;
  }
  unlockAudio();
  prepareLoading.value = true;
  preparingId.value = item.id;
  scanDirection.value = direction === "desc" ? "desc" : "asc";
  preparingDirection.value = scanDirection.value;
  detail.value = null;
  supplierInfo.value = null;
  qrConfig.value = null;
  materialList.value = [];
  boxCode.value = "";
  scannedBoxKeys.value = [];
  submitLoading.value = false;

  try {
    const res = await getSendPlanDetail(item.id);
    if (res?.success === false) {
      throw new Error(res?.msg || res?.message || "获取送货计划单详情失败");
    }
    const data = res?.data || {};
    detail.value = data;
    materialList.value = buildMaterialList(data);

    if (materialList.value.length === 0) {
      notifyError("暂无物料明细");
      ElMessage.warning("该送货计划单暂无物料明细");
      return;
    }

    const supplierId =
      data.supplierId ||
      data.sendDetailList?.[0]?.supplierId ||
      data.sendPlanDetailList?.[0]?.supplierId ||
      data.planDetailList?.[0]?.supplierId ||
      data.outDetailList?.[0]?.supplierId ||
      data.wareOutDetailList?.[0]?.supplierId ||
      data.detailList?.[0]?.supplierId ||
      item.supplierId;

    if (!supplierId) {
      notifyError("缺少供应商");
      ElMessage.error("送货计划单缺少供应商ID，无法加载二维码规则");
      return;
    }

    const supplier = await fetchSupplierConfig(supplierId);
    if (!supplier) {
      notifyError("未找到供应商");
      ElMessage.error(`未找到供应商（ID：${supplierId}）`);
      return;
    }
    supplierInfo.value = supplier;

    const cfg = normalizeQrConfig(supplier.supplierQrConfig);
    if (!cfg || cfg.materialNoIndex < 0) {
      notifyError("规则未配置");
      ElMessage.error(
        `供应商「${supplier.supplierName || supplierId}」未配置有效二维码解析规则`
      );
      return;
    }
    qrConfig.value = cfg;
    viewMode.value = "prepare";
    notifySuccess("请扫描箱码");
    ElMessage.success(`已进入扫码校验（${scanDirectionLabel.value}）`);
    focusBoxInput();
  } catch (e) {
    console.error("进入扫码校验失败:", e);
    notifyError("加载失败");
    ElMessage.error(e?.message || "进入扫码校验失败");
  } finally {
    prepareLoading.value = false;
    preparingId.value = "";
    preparingDirection.value = "";
  }
};

const clearMaterialFailFlags = () => {
  materialList.value.forEach((m) => {
    m.lastFail = false;
  });
};

const handleBoxScan = (rawText) => {
  unlockAudio();
  if (!qrConfig.value) {
    notifyError("规则未加载");
    ElMessage.warning("二维码规则未加载");
    return;
  }

  const text = normalizeScanText(rawText);
  boxCode.value = "";
  if (!text) {
    notifyError("扫码为空");
    ElMessage.error("扫码内容为空");
    focusBoxInput();
    return;
  }

  clearMaterialFailFlags();

  const parsed = parseBoxByConfig(text, qrConfig.value);
  const actual = String(parsed.materialNo || "").trim();
  const boxIndex = String(parsed.boxIndex || "").trim();
  const boxNumStr = String(parsed.num || "").trim();
  const boxNum = toPositiveNumber(boxNumStr);

  if (!actual) {
    notifyError("解析失败");
    ElMessage.error("箱码解析失败：未解析到物料编码");
    focusBoxInput();
    return;
  }

  if (boxIndex) {
    if (scannedBoxKeys.value.includes(boxIndex)) {
      notifyError("已扫过");
      ElMessage.warning(`箱码「${boxIndex}」已扫描，请勿重复扫描`);
      focusBoxInput();
      return;
    }
  }

  const sameMaterial = (m) => String(m.materialNo || "").trim() === actual;
  const target = nextScanItem.value;

  if (!target) {
    notifyError("已全部扫完");
    ElMessage.warning("全部物料已扫完");
    focusBoxInput();
    return;
  }

  const targetNo = String(target.materialNo || "").trim();
  if (targetNo !== actual) {
    const pendingSame = materialList.value.filter(
      (m) => !m.matched && sameMaterial(m)
    );
    const hasMatchedSame = materialList.value.some(
      (m) => m.matched && sameMaterial(m)
    );
    const exists = materialList.value.some(sameMaterial);
    const currentLabel = target.detailSort
      ? `${target.detailSort}（${targetNo}）`
      : targetNo;

    if (pendingSame.length > 0) {
      // 箱码是后面某条明细的编码（含相同物料），不能跳过当前这条
      const nextSame = pendingSame[0];
      const nextLabel = nextSame.detailSort || nextSame.materialNo;
      notifyError("不能跳过");
      ElMessage.warning(
        `当前应扫 ${currentLabel}，扫完后再扫 ${nextLabel}，相同编码也不会提前累计`
      );
      target.lastFail = true;
    } else if (hasMatchedSame) {
      notifyError("已扫过");
      ElMessage.warning(`物料「${actual}」已扫完，请扫 ${currentLabel}`);
      target.lastFail = true;
    } else if (!exists) {
      notifyError("物料不匹配");
      ElMessage.error(
        `物料编码不匹配：当前应扫「${currentLabel}」，箱码是「${actual}」`
      );
      target.lastFail = true;
    } else {
      notifyError("顺序不对");
      ElMessage.warning(`请先扫描 ${currentLabel}，不能跳过`);
      target.lastFail = true;
    }
    focusBoxInput();
    return;
  }

  if (hasQtyRule.value) {
    if (boxNum == null || boxNum <= 0) {
      notifyError("数量无效");
      ElMessage.error("箱码解析失败：未解析到有效数量");
      focusBoxInput();
      return;
    }
    const expected = getExpectedQty(target);
    if (!(expected > 0)) {
      notifyError("数量无效");
      ElMessage.error(`物料「${actual}」缺少有效需求数量，无法校验`);
      focusBoxInput();
      return;
    }
    const nextSum = Number(target.scannedQty || 0) + boxNum;
    if (nextSum > expected) {
      notifyError("数量超限");
      ElMessage.error(
        `数量超限：已扫 ${target.scannedQty || 0} + 本箱 ${boxNum} = ${nextSum}，超过需求 ${expected}`
      );
      target.lastFail = true;
      focusBoxInput();
      return;
    }

    target.scannedQty = nextSum;
    target.scannedMaterialNo = actual;
    target.lastFail = false;
    if (boxIndex) scannedBoxKeys.value.push(boxIndex);

    const lineLabel = target.detailSort ? `${target.detailSort} ` : "";
    if (nextSum >= expected) {
      target.matched = true;
      ElMessage.success(
        `匹配成功：${lineLabel}${actual}，数量 ${nextSum}/${expected}`
      );
    } else {
      ElMessage.success(
        `数量累计：${lineLabel}${actual}，${nextSum}/${expected}，请继续扫本行`
      );
      notifySuccess("请继续扫码");
      focusBoxInput();
      return;
    }
  } else {
    target.matched = true;
    target.lastFail = false;
    target.scannedMaterialNo = actual;
    target.scannedQty = getExpectedQty(target) || 1;
    if (boxIndex) scannedBoxKeys.value.push(boxIndex);
    ElMessage.success(
      `匹配成功：${target.detailSort ? target.detailSort + " " : ""}${actual}`
    );
  }

  if (matchedCount.value >= materialList.value.length) {
    notifySuccess("全部完成");
    ElMessage.success("全部物料已扫码校验完成");
  } else {
    notifySuccess("匹配成功");
  }
  focusBoxInput();
};

const handleBoxScanEnter = () => {
  if (prepareLoading.value || !qrConfigReady.value || allMatched.value) return;
  handleBoxScan(boxCode.value);
};

const handleWareOut = async () => {
  if (!allMatched.value) {
    notifyError("请全部扫码");
    ElMessage.warning("请先完成全部物料扫码后再提交校验");
    return;
  }
  const id = detail.value?.id;
  if (!id) {
    ElMessage.error("缺少送货计划单ID");
    return;
  }
  if (submitLoading.value) return;

  try {
    await ElMessageBox.confirm("确认该送货计划单已校验完成？", "校验完成", {
      confirmButtonText: "校验完成",
      cancelButtonText: "取消",
      type: "warning",
    });
  } catch {
    return;
  }

  submitLoading.value = true;
  try {
    const res = await distributeSendPlanAuto({
      id,
      sendPlanDetailList: materialList.value.map((m) => ({
        materialId: m.materialId,
        materialNo: m.materialNo,
        detailSort: m.detailSort || "",
        isScan: m.matched ? 1 : 0,
      })),
    });
    if (res?.success === false) {
      throw new Error(res?.msg || res?.message || "校验完成失败");
    }
    notifySuccess("校验完成");
    ElMessage.success("校验完成");
    exitPrepare();
    if (scannedSendNo.value) {
      await fetchBySendNo(scannedSendNo.value);
    }
  } catch (e) {
    console.error("校验完成失败:", e);
    notifyError("校验失败");
    ElMessage.error(e?.message || "校验完成失败");
  } finally {
    submitLoading.value = false;
  }
};

const exitPrepare = () => {
  viewMode.value = "list";
  detail.value = null;
  supplierInfo.value = null;
  qrConfig.value = null;
  materialList.value = [];
  boxCode.value = "";
  scannedBoxKeys.value = [];
  submitLoading.value = false;
  focusSendInput();
};

const handleBack = () => {
  if (viewMode.value === "prepare") {
    exitPrepare();
    return;
  }
  router.back();
};

onMounted(() => {
  document.addEventListener("click", unlockAudio, { once: true });
  document.addEventListener("touchstart", unlockAudio, { once: true });
  if ("speechSynthesis" in window) {
    window.speechSynthesis.onvoiceschanged = () => {
      getZhVoice();
    };
  }
  focusSendInput();
});

onUnmounted(() => {
  document.removeEventListener("click", unlockAudio);
  document.removeEventListener("touchstart", unlockAudio);
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
    window.speechSynthesis.onvoiceschanged = null;
  }
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
});
</script>

<style scoped>
.page-container {
  /* 与系统其它页统一：覆盖 Element 默认蓝 */
  --primary-color: #23b1c0;
  --header-background: #23b1c0;
  --header-text: #fff;
  --background-color: #f5f7fa;
  --brand: var(--primary-color);
  --brand-dark: #1c8e9a;
  --brand-soft: #e8f7f9;
  --ok: #67c23a;
  --ok-soft: #f0f9eb;
  --warn: #e6a23c;
  --danger: #f56c6c;
  --text: #303133;
  --muted: #909399;
  --line: #ebeef5;
  --bg: var(--background-color);

  --el-color-primary: var(--primary-color);
  --el-color-primary-light-3: #4fc3cf;
  --el-color-primary-light-5: #7fd4dc;
  --el-color-primary-light-7: #afe4ea;
  --el-color-primary-light-8: #c7ecf0;
  --el-color-primary-light-9: #dff4f6;
  --el-color-primary-dark-2: #1c8e9a;

  min-height: 100vh;
  background: var(--bg);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  padding-bottom: calc(18px + env(safe-area-inset-bottom));
}

.page-container.is-prepare {
  padding-bottom: calc(88px + env(safe-area-inset-bottom));
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  border: none;
  background: transparent;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-back {
  width: 20px;
  height: 20px;
}

.title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--header-text);
}

.header-right {
  width: 36px;
}

.content {
  padding: 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.block-card {
  margin-bottom: 8px;
}

.compact-card :deep(.el-card__body) {
  padding: 10px;
}

.full-btn {
  width: 100%;
  height: auto;
  white-space: normal;
  line-height: 1.3;
}

.send-no {
  word-break: break-all;
}

.order-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.order-meta {
  display: block;
  margin-top: 6px;
}

.action-row {
  margin-top: 10px;
}

.list-wrap {
  min-height: 120px;
}

.prepare-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.progress-row {
  margin-bottom: 4px;
}

.box-input {
  margin-top: 8px;
}

.scan-hint {
  display: block;
  margin-top: 6px;
}

.sticky-scan {
  position: sticky;
  top: calc(52px + env(safe-area-inset-top) + 4px);
  z-index: 20;
}

.tag-end {
  text-align: right;
}

.material-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
}

.material-row.current {
  border-color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.material-row.later {
  opacity: 0.55;
}

.material-row.matched {
  border-color: var(--el-color-success-light-5);
  background: var(--ok-soft);
}

.material-row.failed {
  border-color: var(--el-color-danger-light-5);
  background: #fef0f0;
}

.sort-tag {
  flex-shrink: 0;
  min-width: 36px;
  justify-content: center;
}

.material-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.material-no {
  font-size: 15px;
  word-break: break-all;
  line-height: 1.25;
}

.action-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid var(--line);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.action-bar :deep(.el-text) {
  flex: 1;
}
</style>
